import { SeasonFragment, SeasonLeagueFragment } from '@amf/shared/types/graphql';
import useOffenseList from '@amf/shared/components/offense/hooks/useOffenseList';
import { useTranslate } from '@tolgee/react';
import Loader from '@amf/shared/components/utils/Loader';
import Empty from '@amf/shared/components/utils/Empty';
import React, { useEffect, useMemo, useState } from 'react';
import OffenseListItem from '@amf/shared/components/offense/OffenseListItem';
import SeasonSelect, { SeasonSelectData } from '@amf/shared/components/menu/SeasonSelect';
import { findTeamDefaults } from '@amf/shared/data/Team';

type Props = {
  league?: SeasonLeagueFragment;
  seasons: SeasonFragment[];
  prefixes: { offenses: string };
  playerId?: string;
};

function OffenseTable({ league: defaultLeague, seasons, prefixes, playerId }: Props) {
  const { t } = useTranslate();

  const defaultValues: SeasonSelectData = useMemo(() => {
    const { league, season } = findTeamDefaults(undefined, seasons);
    return {
      league: defaultLeague?.id ?? league?.id ?? seasons[0]?.leagues[0]?.id,
      season: season?.id ?? seasons[0]?.id,
    };
  }, [defaultLeague, seasons]);

  const [filter, setFilter] = useState<SeasonSelectData>(defaultValues);
  const { offenses, loading } = useOffenseList(filter.league, filter.season, playerId ?? null);

  useEffect(() => setFilter(defaultValues), [defaultValues]);

  return (
    <div className='OffenseTable'>
      {!defaultLeague && (
        <div className='TeamStats__header'>
          <h2 className='TeamStats__title'>{t('offense.list.title')}</h2>
          <SeasonSelect
            values={defaultValues}
            seasons={seasons}
            onUpdate={async data => setFilter(data)}
            allSeasonsAllowed
            allLeaguesAllowed
          />
        </div>
      )}
      {loading && <Loader size={defaultLeague ? 'normal' : 'small'}>{t('offense.loading')}</Loader>}
      {!loading &&
        offenses.map(offense => (
          <OffenseListItem key={offense.id} offense={offense} prefixes={prefixes} />
        ))}
      {!loading && offenses.length === 0 && (
        <Empty size={defaultLeague ? 'normal' : 'small'}>{t('offense.empty')}</Empty>
      )}
    </div>
  );
}

export default OffenseTable;
